import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Slider,
  Tabs,
  Tab,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import {
  Send,
  AutoAwesome,
  Compress,
  History,
  ContentCopy,
  ExpandMore,
  Psychology,
  AllInclusive,
  Download,
  Upload,
  SaveAlt
} from '@mui/icons-material';
import {
  NicheAgentMetadata,
  NicheAgentSession,
  NicheAgentIteration,
  SubAgent,
  NICHE_AGENTS
} from '../models/nicheAgents';
import { createNicheAgentService, NicheAgentService } from '../services/nicheAgentService';
import { NicheAgentTemplate, getTemplatesByAgent } from '../models/nicheAgentTemplates';
import PromptDisplay from './PromptDisplay';

interface NicheAgentIteratorProps {
  agentType?: string;
}

const NicheAgentIterator: React.FC<NicheAgentIteratorProps> = ({ agentType: propAgentType }) => {
  const { agentType: routeAgentType } = useParams<{ agentType: string }>();
  const agentType = propAgentType || routeAgentType;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [session, setSession] = useState<NicheAgentSession | null>(null);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [selectedSubAgent, setSelectedSubAgent] = useState<SubAgent | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [compactionLevel, setCompactionLevel] = useState<'Minimal' | 'Moderate' | 'Aggressive'>('Moderate');
  
  const [agentMetadata, setAgentMetadata] = useState<NicheAgentMetadata | null>(null);
  const [agentService, setAgentService] = useState<NicheAgentService | null>(null);
  const [templates, setTemplates] = useState<NicheAgentTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    if (agentType) {
      const metadata = NICHE_AGENTS.find(agent => agent.id === agentType);
      if (metadata) {
        setAgentMetadata(metadata);
        setAgentService(createNicheAgentService(metadata));
        setSelectedSubAgent(metadata.subAgents[0]);
        setTemplates(getTemplatesByAgent(metadata.id));
        loadStoredSession(metadata.id);
      }
    }
  }, [agentType]);

  const loadStoredSession = (agentId: string) => {
    const storedSession = localStorage.getItem(`nicheAgent_${agentId}_session`);
    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);
        // Ensure iterations array exists
        const sessionWithIterations = {
          ...parsed,
          iterations: parsed.iterations || []
        };
        setSession(sessionWithIterations);
        setInitialPrompt(parsed.currentPrompt || '');
      } catch (e) {
        console.error('Failed to load stored session:', e);
      }
    }
  };

  const saveSession = (newSession: NicheAgentSession) => {
    if (agentMetadata) {
      localStorage.setItem(`nicheAgent_${agentMetadata.id}_session`, JSON.stringify(newSession));
    }
    setSession(newSession);
  };

  const handleStartSession = async () => {
    if (!initialPrompt.trim() || !agentService) return;

    setLoading(true);
    setError(null);

    try {
      const newSession = await agentService.startSession({ initialPrompt });
      saveSession(newSession);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async (subAgent?: SubAgent) => {
    if (!session || !agentService) return;

    setLoading(true);
    setError(null);

    try {
      const agent = subAgent || selectedSubAgent;
      if (!agent) return;

      const response = await agentService.iterate(session.sessionId, {
        agentType: agent.name.replace(/\s+/g, ''),
        detailDomain: agent.detailDomain,
        maxResponseTokens: maxTokens,
        temperature
      });

      // Handle both old and new response formats
      const refinedPromptContent = response.refinedPrompt || response.improvedPrompt || '';
      const previousPromptContent = response.previousPrompt || session.currentPrompt;
      
      const updatedSession: NicheAgentSession = {
        ...session,
        currentPrompt: refinedPromptContent,
        iterations: [
          ...(session.iterations || []),
          {
            iterationNumber: response.iterationNumber,
            agentType: response.agentType,
            detailDomain: response.detailDomain,
            previousPrompt: previousPromptContent,
            refinedPrompt: refinedPromptContent,
            improvementExplanation: response.improvementExplanation || 'Prompt refined by ' + response.agentType,
            timestamp: new Date().toISOString()
          }
        ]
      };

      saveSession(updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to iterate');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkIterate = async () => {
    if (!session || !agentService) return;

    setLoading(true);
    setError(null);

    try {
      const response = await agentService.bulkIterate(session.sessionId, {
        agentType: 'Bulk',
        maxResponseTokens: maxTokens * 2,
        temperature
      });

      // Process iterations to ensure they have refinedPrompt
      const processedIterations = response.iterations.map(iter => ({
        ...iter,
        refinedPrompt: iter.refinedPrompt || iter.improvedPrompt || '',
        timestamp: iter.timestamp || new Date().toISOString()
      }));

      const updatedSession: NicheAgentSession = {
        ...session,
        currentPrompt: response.finalPrompt || processedIterations[processedIterations.length - 1]?.refinedPrompt || session.currentPrompt,
        iterations: [...(session.iterations || []), ...processedIterations]
      };

      saveSession(updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to bulk iterate');
    } finally {
      setLoading(false);
    }
  };

  const handleCompactPrompt = async () => {
    if (!session || !agentService) return;

    setLoading(true);
    setError(null);

    try {
      const response = await agentService.compactPrompt(session.sessionId, {
        compactionLevel,
        targetTokenCount: Math.floor(maxTokens * 0.7)
      });

      // Handle the actual response format from the API
      const compactedPromptContent = response.compactedPrompt || (response as any).improvedPrompt || '';
      const originalPromptContent = response.originalPrompt || session.currentPrompt;
      const compressionInfo = response.compressionRatio 
        ? `Compressed from ${response.originalTokenCount} to ${response.compactedTokenCount} tokens (${Math.round(response.compressionRatio * 100)}% reduction)`
        : 'Prompt compacted successfully';

      const updatedSession: NicheAgentSession = {
        ...session,
        currentPrompt: compactedPromptContent,
        iterations: [
          ...(session.iterations || []),
          {
            iterationNumber: (response as any).iterationNumber || (session.iterations?.length || 0) + 1,
            agentType: (response as any).agentType || 'Compactor',
            previousPrompt: originalPromptContent,
            refinedPrompt: compactedPromptContent,
            improvementExplanation: compressionInfo,
            timestamp: new Date().toISOString()
          }
        ]
      };

      saveSession(updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to compact prompt');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const clearSession = () => {
    if (agentMetadata) {
      localStorage.removeItem(`nicheAgent_${agentMetadata.id}_session`);
      setSession(null);
      setInitialPrompt('');
    }
  };

  const exportSession = () => {
    if (!session || !agentMetadata) return;

    const exportData = {
      agentType: agentMetadata.id,
      agentName: agentMetadata.name,
      session: session,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agentMetadata.id}_session_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importSession = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !agentMetadata) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target?.result as string);
        
        if (importData.agentType !== agentMetadata.id) {
          setError(`This session file is for ${importData.agentName} agent, not ${agentMetadata.name}`);
          return;
        }

        if (importData.session) {
          // Ensure iterations array exists
          const sessionWithIterations = {
            ...importData.session,
            iterations: importData.session.iterations || []
          };
          saveSession(sessionWithIterations);
          setInitialPrompt(importData.session.currentPrompt || '');
        }
      } catch (err) {
        setError('Failed to import session file. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  if (!agentMetadata) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Agent type not found: {agentType}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: agentMetadata.color }}>
          {agentMetadata.name}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {agentMetadata.description}
        </Typography>

        {!session ? (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Initial Prompt"
              value={initialPrompt}
              onChange={(e) => setInitialPrompt(e.target.value)}
              placeholder={`Describe what you want to visualize...`}
              sx={{ mb: 2 }}
            />
            
            {templates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowTemplates(!showTemplates)}
                  startIcon={<SaveAlt />}
                >
                  {showTemplates ? 'Hide Templates' : `Use Template (${templates.length} available)`}
                </Button>
                
                {showTemplates && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {templates.map((template) => (
                      <Grid item xs={12} sm={6} key={template.id}>
                        <Card 
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 2 }
                          }}
                          onClick={() => {
                            setInitialPrompt(template.prompt);
                            setShowTemplates(false);
                          }}
                        >
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              {template.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {template.description}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {template.tags.map(tag => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
            
            <Button
              variant="contained"
              size="large"
              onClick={handleStartSession}
              disabled={loading || !initialPrompt.trim()}
              startIcon={<Send />}
              sx={{ backgroundColor: agentMetadata.color }}
            >
              Start Session
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Current Prompt</Typography>
                <Chip label={`${session.iterations?.length || 0} iterations`} size="small" />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Copy prompt">
                  <IconButton onClick={() => copyToClipboard(session.currentPrompt)} size="small">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export session">
                  <IconButton onClick={exportSession} size="small">
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Import session">
                  <IconButton component="label" size="small">
                    <Upload />
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={importSession}
                    />
                  </IconButton>
                </Tooltip>
                <Button variant="outlined" size="small" onClick={clearSession}>
                  Clear Session
                </Button>
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
                <PromptDisplay 
                  content={session.currentPrompt || ''} 
                  onCopy={copyToClipboard}
                />
              </Paper>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Sub-Agents" icon={<Psychology />} iconPosition="start" />
                  <Tab label="Bulk Operations" icon={<AllInclusive />} iconPosition="start" />
                  <Tab label="History" icon={<History />} iconPosition="start" />
                </Tabs>

                {selectedTab === 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Select Sub-Agent for Refinement
                    </Typography>
                    <Grid container spacing={2}>
                      {agentMetadata.subAgents.map((subAgent) => (
                        <Grid item xs={12} sm={6} key={subAgent.id}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: selectedSubAgent?.id === subAgent.id ? 2 : 1,
                              borderColor: selectedSubAgent?.id === subAgent.id ? agentMetadata.color : 'divider',
                              '&:hover': { boxShadow: 3 }
                            }}
                            onClick={() => setSelectedSubAgent(subAgent)}
                          >
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {subAgent.name}
                                </Typography>
                                <Tooltip 
                                  title={`Domain: ${subAgent.detailDomain}\n\nThis sub-agent specializes in ${subAgent.description.toLowerCase()}. Click to select for targeted refinement.`}
                                  arrow
                                  placement="top"
                                >
                                  <IconButton size="small" sx={{ p: 0 }}>
                                    <Psychology fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                {subAgent.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleIterate()}
                        disabled={loading || !selectedSubAgent}
                        startIcon={<AutoAwesome />}
                        sx={{ backgroundColor: agentMetadata.color }}
                      >
                        Refine with {selectedSubAgent?.name}
                      </Button>
                    </Box>
                  </Box>
                )}

                {selectedTab === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Bulk Operations
                    </Typography>
                    <Stack spacing={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Bulk Iterate All Sub-Agents
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Automatically iterate through all 10 sub-agents to comprehensively enhance your prompt.
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={handleBulkIterate}
                            disabled={loading}
                            startIcon={<AllInclusive />}
                            sx={{ backgroundColor: agentMetadata.color }}
                          >
                            Bulk Iterate All Agents
                          </Button>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Compact Prompt
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Reduce prompt size while maintaining essential details.
                          </Typography>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Compaction Level</InputLabel>
                            <Select
                              value={compactionLevel}
                              onChange={(e) => setCompactionLevel(e.target.value as any)}
                              label="Compaction Level"
                            >
                              <MenuItem value="Minimal">Minimal - Preserve most details</MenuItem>
                              <MenuItem value="Moderate">Moderate - Balance size and detail</MenuItem>
                              <MenuItem value="Aggressive">Aggressive - Maximum compression</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            onClick={handleCompactPrompt}
                            disabled={loading}
                            startIcon={<Compress />}
                          >
                            Compact Prompt
                          </Button>
                        </CardContent>
                      </Card>
                    </Stack>
                  </Box>
                )}

                {selectedTab === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Iteration History
                    </Typography>
                    {!session.iterations || session.iterations.length === 0 ? (
                      <Typography color="text.secondary">No iterations yet</Typography>
                    ) : (
                      <Stack spacing={2}>
                        {session.iterations.map((iteration, index) => (
                          <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                <Chip 
                                  label={`#${iteration.iterationNumber}`} 
                                  size="small" 
                                  color="primary"
                                />
                                <Typography variant="subtitle2">
                                  {iteration.agentType}
                                  {iteration.detailDomain && ` - ${iteration.detailDomain}`}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(iteration.timestamp).toLocaleString()}
                                </Typography>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={2}>
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Improvement Explanation:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {iteration.improvementExplanation}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Refined Prompt:
                                  </Typography>
                                  <Paper variant="outlined" sx={{ p: 2 }}>
                                    <PromptDisplay 
                                      content={iteration.refinedPrompt || iteration.improvedPrompt || ''} 
                                      onCopy={copyToClipboard}
                                    />
                                  </Paper>
                                </Box>
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Settings
                  </Typography>
                  <Stack spacing={3}>
                    <Box>
                      <Typography gutterBottom>Temperature: {temperature}</Typography>
                      <Slider
                        value={temperature}
                        onChange={(e, v) => setTemperature(v as number)}
                        min={0}
                        max={1}
                        step={0.1}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom>Max Tokens: {maxTokens}</Typography>
                      <Slider
                        value={maxTokens}
                        onChange={(e, v) => setMaxTokens(v as number)}
                        min={512}
                        max={4096}
                        step={256}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default NicheAgentIterator;